import i18n from './i18n';
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./i18n";
import App from "./App";

// Add CSS to prevent some ResizeObserver loops
const resizeObserverCSS = `
  /* Prevent ResizeObserver loops from dropdown animations */
  [data-radix-select-content] {
    will-change: auto !important;
  }
  
  /* Stabilize select dropdowns */
  [role="combobox"], [role="listbox"] {
    contain: layout style !important;
  }
  
  /* Prevent layout thrashing in forms */
  input, select, textarea {
    contain: layout !important;
  }
`;

// Inject CSS
const style = document.createElement('style');
style.textContent = resizeObserverCSS;
document.head.appendChild(style);

// React Error Boundary for ResizeObserver errors
class ResizeObserverErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Check if it's a ResizeObserver error
    if (error.message && error.message.includes('ResizeObserver loop completed')) {
      return { hasError: false }; // Don't show error UI for ResizeObserver issues
    }
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log other errors but ignore ResizeObserver errors
    if (!error.message || !error.message.includes('ResizeObserver loop completed')) {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return <div>{i18n.t('auto.misc3.somethingWentWrong')}</div>;
    }
    return this.props.children;
  }
}

// Ultra-aggressive ResizeObserver error suppression
const suppressResizeObserverErrors = () => {
  // Method 1: Override ALL console methods
  const originalMethods = {};
  ['error', 'warn', 'log', 'info', 'debug'].forEach(method => {
    originalMethods[method] = console[method];
    console[method] = (...args) => {
      const message = args[0];
      if (
        (typeof message === 'string' && message.includes('ResizeObserver loop completed')) ||
        (message && message.message && message.message.includes('ResizeObserver loop completed'))
      ) {
        return; // Silently ignore all ResizeObserver-related console output
      }
      originalMethods[method].apply(console, args);
    };
  });

  // Method 2: Multiple window error event handlers
  const errorHandler = (event) => {
    if (
      (event.message && event.message.includes('ResizeObserver loop completed')) ||
      (event.error && event.error.message && event.error.message.includes('ResizeObserver loop completed'))
    ) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  };

  window.addEventListener('error', errorHandler, true);
  window.addEventListener('error', errorHandler, false);
  document.addEventListener('error', errorHandler, true);

  // Method 3: Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (
      event.reason && 
      ((typeof event.reason === 'string' && event.reason.includes('ResizeObserver loop completed')) ||
       (event.reason.message && event.reason.message.includes('ResizeObserver loop completed')))
    ) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  });

  // Method 4: Override React's error handling (aggressive approach)
  if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    const originalOnErrorOrWarning = window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onErrorOrWarning;
    if (originalOnErrorOrWarning) {
      window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onErrorOrWarning = (...args) => {
        const error = args[0];
        if (
          error && 
          ((typeof error === 'string' && error.includes('ResizeObserver loop completed')) ||
           (error.message && error.message.includes('ResizeObserver loop completed')))
        ) {
          return; // Ignore ResizeObserver errors in React DevTools
        }
        return originalOnErrorOrWarning.apply(this, args);
      };
    }
  }

  // Method 5: Enhanced ResizeObserver polyfill with better error handling
  if (window.ResizeObserver) {
    const OriginalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class extends OriginalResizeObserver {
      constructor(callback) {
        const wrappedCallback = (entries, observer) => {
          // Use requestAnimationFrame to avoid sync layout thrashing
          requestAnimationFrame(() => {
            try {
              callback(entries, observer);
            } catch (error) {
              if (
                error.message && 
                error.message.includes('ResizeObserver loop completed')
              ) {
                // Silently ignore - this is expected behavior in some cases
                return;
              }
              // Re-throw other errors
              throw error;
            }
          });
        };
        super(wrappedCallback);
      }
    };
  }

  // Method 6: Override window.onerror as last resort
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (
      (typeof message === 'string' && message.includes('ResizeObserver loop completed')) ||
      (error && error.message && error.message.includes('ResizeObserver loop completed'))
    ) {
      return true; // Prevent default error handling
    }
    if (originalOnError) {
      return originalOnError.call(window, message, source, lineno, colno, error);
    }
    return false;
  };
};

// Initialize ultra-aggressive error suppression immediately
suppressResizeObserverErrors();

// ── Dynamic favicon from company_profile ─────────────────────────
// Fetches the public branding endpoint (no auth) and swaps out the
// `<link rel="icon">` in the document head. Also listens for a
// `company-branding-changed` event so admin uploads reflect
// immediately without a page reload.
const applyDynamicFavicon = async () => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) return;
    const resp = await fetch(`${backendUrl}/api/company/profile/public`);
    if (!resp.ok) return;
    const data = await resp.json();
    if (data.favicon_file) {
      const faviconUrl = (file) => `${backendUrl}/api/company/favicon/${file}`;
      const variants = data.favicon_variants || {};

      // Remove any prior <link rel="icon"> / apple-touch-icon.
      document
        .querySelectorAll('link[rel*="icon"]')
        .forEach((el) => el.parentNode && el.parentNode.removeChild(el));

      const addLink = (rel, href, opts = {}) => {
        const link = document.createElement('link');
        link.rel = rel;
        link.href = href;
        if (opts.type) link.type = opts.type;
        if (opts.sizes) link.sizes = opts.sizes;
        document.head.appendChild(link);
      };

      // Prefer generated .ico for the classic favicon slot (widest support).
      if (variants.ico) {
        addLink('icon', faviconUrl(variants.ico), { type: 'image/x-icon' });
      }
      // Modern PNG variants for high-DPI displays.
      if (variants.png_32) {
        addLink('icon', faviconUrl(variants.png_32), { type: 'image/png', sizes: '32x32' });
      }
      if (variants.png_16) {
        addLink('icon', faviconUrl(variants.png_16), { type: 'image/png', sizes: '16x16' });
      }
      // Apple touch icon (iOS home-screen).
      if (variants.png_180) {
        addLink('apple-touch-icon', faviconUrl(variants.png_180), { sizes: '180x180' });
      }
      // Always ensure at least the source file is registered as a fallback.
      addLink('icon', faviconUrl(data.favicon_file));

      // PWA manifest — points to the dynamic backend endpoint so it stays in
      // sync with the current favicon variants + company name.
      document
        .querySelectorAll('link[rel="manifest"]')
        .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
      const manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      manifestLink.href = `${backendUrl}/api/company/manifest.webmanifest`;
      // `use-credentials` is not needed — the endpoint is public.
      document.head.appendChild(manifestLink);

      // Update / add the theme-color meta so the mobile browser chrome
      // matches the app's primary hue.
      let theme = document.querySelector('meta[name="theme-color"]');
      if (!theme) {
        theme = document.createElement('meta');
        theme.name = 'theme-color';
        document.head.appendChild(theme);
      }
      theme.content = '#1e40af';
    }
    if (data.name) {
      // Nice-to-have: put the company name in the tab title too.
      document.title = data.name;
    }
  } catch (_) {
    // Non-critical — the app works fine with the default favicon.
  }
};
applyDynamicFavicon();
window.addEventListener('company-branding-changed', applyDynamicFavicon);

// ── Register the PWA service worker (production only) ────────────
// The SW caches the app shell + static bundle for offline reads and
// keeps favicon/manifest fresh with stale-while-revalidate. API calls
// are never cached to avoid stale/authenticated data leaks.
if (
  'serviceWorker' in navigator &&
  process.env.NODE_ENV === 'production'
) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .catch((err) => {
        // Non-critical — the app still works without offline support.
        console.warn('[PWA] Service worker registration failed:', err);
      });
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));

// Disable StrictMode to reduce ResizeObserver issues in development
const isProduction = process.env.NODE_ENV === 'production';

root.render(
  isProduction ? (
    <React.StrictMode>
      <ResizeObserverErrorBoundary>
        <App />
      </ResizeObserverErrorBoundary>
    </React.StrictMode>
  ) : (
    <ResizeObserverErrorBoundary>
      <App />
    </ResizeObserverErrorBoundary>
  ),
);
